import { Box, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";
import { useGetStoreInventoryQuery, useGetCategoryListQuery, useUpdateStoreInventoryStatusMutation } from "../../store/api/manageAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import { showError, showToast } from "../../constants/toasts";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const StoreInventory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get storeId from location state, URL params, or search params
  const storeIdFromState = (location.state as any)?.storeId;
  const storeIdFromUrl = searchParams.get("storeId");
  const storeId = storeIdFromState || storeIdFromUrl || "";

  // Debug: Log storeId to help troubleshoot
  useEffect(() => {
    if (storeId) {
      console.log("Store Inventory - StoreId:", storeId);
    } else {
      console.warn("Store Inventory - StoreId is missing!");
    }
  }, [storeId]);

  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1); // Reset to first page when changing tabs
    setCategoryId(""); // Reset category filter
  };

  // Map tabs to API types
  const typeMap: Record<number, "topping" | "flavor" | "product" | "boxart"> = {
    0: "flavor",
    1: "topping",
    2: "product",
    3: "boxart",
  };

  const currentType = typeMap[valueTabs];

  const [categoryId, setCategoryId] = React.useState<string>("");
  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategoryId(event.target.value as string);
    setPage(1); // Reset to first page when changing category
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Fetch inventory data
  const { data, isLoading, error, refetch } = useGetStoreInventoryQuery(
    {
      type: currentType,
      storeId: storeId || "",
      page,
      limit,
      search: debouncedSearchTerm || undefined,
      categoryId: categoryId || undefined,
    },
    { skip: !storeId || !currentType }
  );

  // Update inventory status mutation
  const [updateInventoryStatus, { isLoading: isUpdatingStatus }] = useUpdateStoreInventoryStatusMutation();

  // Fetch categories for products tab
  const { data: categoryData } = useGetCategoryListQuery(
    { page: 1, limit: 100 },
    { skip: valueTabs !== 2 } // Only fetch when on products tab
  );

  // Show error if storeId is missing
  useEffect(() => {
    if (!storeId) {
      showError("Store ID is missing. Please select a store from the store list.");
      // Don't navigate immediately, give user a chance to see the error
      const timer = setTimeout(() => {
        navigate("/manage-stores");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [storeId, navigate]);

  // Show error message if API call fails
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.data?.message || "Failed to fetch inventory. Please try again.";
      showError(errorMessage);
    }
  }, [error]);

  // Extract inventory items from API response
  const rows = useMemo(() => {
    // Debug: Log API response structure
    if (data) {
      console.log("Inventory API Response:", data);
    }
    
    if (!data?.data) {
      return [];
    }

    // Map tab types to API response keys
    const responseKeyMap: Record<string, string> = {
      flavor: "storeflavor",
      topping: "storetopping",
      product: "storeproduct",
      boxart: "storeboxart",
    };

    const responseKey = responseKeyMap[currentType];
    console.log("Response key for type", currentType, ":", responseKey);
    console.log("Available keys in data.data:", Object.keys(data.data || {}));
    
    const items = data.data[responseKey] || [];
    console.log("Items found:", items.length, items);

    if (Array.isArray(items) && items.length > 0) {
      console.log(`Processing ${items.length} items for type: ${currentType}`, items);
      const mappedRows = items.map((item: any) => {
        // Handle nested structure - flavorDetails, toppingDetails, productDetails, boxDetails
        const details = item.flavorDetails || item.toppingDetails || item.productDetails || item.boxDetails || [];
        const detailItem = Array.isArray(details) && details.length > 0 ? details[0] : item;
        
        const mappedItem = {
          id: item._id || item.id, // Use the store inventory item ID (not the nested detail ID)
          storeInventoryId: item._id || item.id, // Store this for API calls
          detailId: detailItem._id || detailItem.id, // The actual flavor/topping/product ID - this is what we need for the API
          name: detailItem.name || item.name || "N/A",
          image: detailItem.image || item.image || "/static/images/placeholder.png",
          category: detailItem.categoryDetails?.name || item.categoryDetails?.name || detailItem.category || item.category || "N/A",
          
          status: item.isActive !== undefined ? item.isActive : (item.isOutOfStock === false),
        };
        
        console.log("Mapped item:", mappedItem, "from item:", item, "detailItem:", detailItem);
        return mappedItem;
      });
      console.log("Final rows array:", mappedRows);
      return mappedRows;
    }
    console.log("No items found or items is not an array. Items:", items, "Is array:", Array.isArray(items));
    return [];
  }, [data, currentType]);

  // Debug: Log rows after computation
  useEffect(() => {
    console.log("Rows computed:", rows.length, rows);
  }, [rows]);

  // Extract categories for dropdown
  const categories = useMemo(() => {
    if (categoryData?.data?.list && Array.isArray(categoryData.data.list)) {
      return categoryData.data.list.map((cat: any) => ({
        id: cat._id || cat.id,
        name: cat.name || "N/A",
      }));
    }
    return [];
  }, [categoryData]);

  // Calculate total pages
  const totalCount = data?.data?.total || data?.data?.totalCount || data?.totalCount || 0;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / limit) : 0;

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle inventory status toggle
  const handleStatusToggle = async (itemId: string, itemName: string, currentStatus: boolean) => {
    if (!currentType) {
      showError("Type is missing");
      return;
    }

    if (!itemId) {
      showError("Item ID is missing");
      return;
    }

    // currentStatus represents isActive: true = active/in stock, false = inactive/out of stock
    // Toggle the status (same as flavor page)
    const newStatus = !currentStatus;
    
    try {
      const response = await updateInventoryStatus({
        type: currentType,
        itemId: itemId,
        isActive: newStatus,
      }).unwrap();
      
      const successMessage = response?.message || 
        `${itemName} ${newStatus ? "marked as in stock" : "marked as out of stock"} successfully`;
      showToast(successMessage);
      
      // Refetch the inventory list to get updated data
      refetch();
    } catch (error: any) {
      console.error("Error updating inventory status:", error);
      console.error("Error details:", {
        status: error?.status,
        data: error?.data,
        error: error?.error,
      });
      
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        "Failed to update inventory status. Please try again.";
      
      showError(errorMessage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isUpdatingStatus} />
      <div className="main_title">
        <h1>Store Inventory</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - {" "}
          <button onClick={() => navigate("/manage-stores")}>Store Management</button> -
          Store Inventory
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Flavors" {...a11yProps(0)} />
              <Tab label="Toppings" {...a11yProps(1)} />
              <Tab label="Products" {...a11yProps(2)} />
              <Tab label="Box Art" {...a11yProps(3)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards view_page">
        <CustomTabPanel value={valueTabs} index={0}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                placeholder="Search..."
              />
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No</TableCell>
                  <TableCell>Flavor Name</TableCell>
                  <TableCell>Out of Stock</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Loading flavors...
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No flavors found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>
                        <Box component="a" className="user_block">
                          <figure>
                            <img src={row.image} alt={row.name} />
                          </figure>
                          <p>
                            <b>{row.name}</b>
                          </p>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SwitchToggle 
                          checked={row.status} 
                          onChange={() => handleStatusToggle(row.detailId || row.storeInventoryId || row.id, row.name, row.status)}
                          disabled={isUpdatingStatus || isLoading}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 0 && (
            <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
              <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
                Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Pagination
                  module={rows}
                  page={page}
                  onPageChange={handlePageChange}
                  totalPages={totalPages}
                  setPage={setPage}
                  totalCount={totalCount}
                  limit={limit}
                />
              </div>
            </div>
          )}
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                placeholder="Search..."
              />
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No</TableCell>
                  <TableCell>Topping Name</TableCell>
                  <TableCell>Out of Stock</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Loading toppings...
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No toppings found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>
                        <Box component="a" className="user_block">
                          <figure>
                            <img src={row.image} alt={row.name} />
                          </figure>
                          <p>
                            <b>{row.name}</b>
                          </p>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SwitchToggle 
                          checked={row.status} 
                          onChange={() => handleStatusToggle(row.detailId || row.storeInventoryId || row.id, row.name, row.status)}
                          disabled={isUpdatingStatus || isLoading}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 0 && (
            <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
              <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
                Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Pagination
                  module={rows}
                  page={page}
                  onPageChange={handlePageChange}
                  totalPages={totalPages}
                  setPage={setPage}
                  totalCount={totalCount}
                  limit={limit}
                />
              </div>
            </div>
          )}
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                placeholder="Search..."
              />
              <div className="form">
                <div className="control_group">
                  <Select
                    fullWidth
                    displayEmpty
                    value={categoryId}
                    onChange={handleCategoryChange}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#999' }}>Select Category</span>;
                      }
                      const selectedCategory = categories.find(cat => cat.id === selected);
                      return selectedCategory?.name || selected;
                    }}
                  >
                    <MenuItem disabled value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Out of Stock</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading products...
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>
                        <Box component="a" className="click_action user_block" onClick={() => navigate('/manage-products/detail')}>
                          <figure>
                            <img src={row.image} alt={row.name} />
                          </figure>
                          <p>
                            <b>{row.name}</b>
                          </p>
                        </Box>
                      </TableCell>
                      <TableCell>{row.category}</TableCell>
                      <TableCell>
                        <SwitchToggle 
                          checked={row.status} 
                          onChange={() => handleStatusToggle(row.detailId || row.storeInventoryId || row.id, row.name, row.status)}
                          disabled={isUpdatingStatus || isLoading}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 0 && (
            <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
              <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
                Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Pagination
                  module={rows}
                  page={page}
                  onPageChange={handlePageChange}
                  totalPages={totalPages}
                  setPage={setPage}
                  totalCount={totalCount}
                  limit={limit}
                />
              </div>
            </div>
          )}
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                placeholder="Search..."
              />
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No</TableCell>
                  <TableCell>Box Art Name</TableCell>
                  <TableCell>Out of Stock</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Loading box art...
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No box art found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>
                        <Box component="a" className="click_action user_block" onClick={() => navigate('/manage-products/detail')}>
                          <figure>
                            <img src={row.image} alt={row.name} />
                          </figure>
                          <p>
                            <b>{row.name}</b>
                          </p>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SwitchToggle 
                          checked={row.status} 
                          onChange={() => handleStatusToggle(row.detailId || row.storeInventoryId || row.id, row.name, row.status)}
                          disabled={isUpdatingStatus || isLoading}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 0 && (
            <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
              <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
                Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Pagination
                  module={rows}
                  page={page}
                  onPageChange={handlePageChange}
                  totalPages={totalPages}
                  setPage={setPage}
                  totalCount={totalCount}
                  limit={limit}
                />
              </div>
            </div>
          )}
        </CustomTabPanel>
      </div>
    </>
  );
};

export default StoreInventory;
